import type { Post } from '../types';
import { normalizePostCategory } from '../types';

/**
 * Structure-marker post badge. Encoded as a primitive union (not an object)
 * so per-marker Zustand selectors can compare by value — see
 * StructureClusterMarker (native) and useMarkerLayer.web.ts (web).
 *
 * - 'delta': the structure has a recent public before/after post — the
 *   established "delta = before/after" glyph (TRIANGLE_PATH).
 * - 'risk' / 'progress': no before/after post, colored by the latest post's
 *   category.
 *
 * undefined (absent from the returned record) = no badge.
 */
export type StructureBadge = 'delta' | 'risk' | 'progress';

/**
 * Derive a structureId -> badge map from the map's live post feed
 * (postsStore.mapPosts — public + 120-day window, createdAt desc, no new
 * Firestore reads). Mirrors floatingMapPosts' defensive visibility check
 * (useMapSubscriptions.ts): a local `updatePost` patch can flip a cached
 * mapPost to private before the server echo removes it from the snapshot.
 *
 * Does NOT require `p.location` — the badge is about the structure, not the
 * post's own coordinates (structure-linked posts render no floating pin of
 * their own).
 */
export function deriveStructurePostBadges(mapPosts: Post[]): Record<string, StructureBadge> {
  const hasDelta = new Set<string>();
  // First occurrence per structureId wins for "latest category" — mapPosts
  // is createdAt desc, so the first matching post for a given structure is
  // its most recent.
  const latestCategory: Record<string, StructureBadge> = {};

  for (const p of mapPosts) {
    if (!p.structureId) continue;
    if (!(p.visibility === 'public' || p.visibility === undefined)) continue;
    // News never counts toward the badge — the grammar is one attribute
    // (delta/risk/progress), and a news post is neither.
    if (p.category === 'news') continue;

    if (p.beforeImageUrl || p.beforePostId) {
      hasDelta.add(p.structureId);
    }
    if (!(p.structureId in latestCategory)) {
      latestCategory[p.structureId] =
        normalizePostCategory(p.category) === 'risk' ? 'risk' : 'progress';
    }
  }

  // Every post considered above sets latestCategory for its structureId (on
  // its first occurrence), so hasDelta is always a subset of latestCategory's
  // keys — a single pass over latestCategory covers every badged structure.
  const badges: Record<string, StructureBadge> = {};
  for (const structureId in latestCategory) {
    badges[structureId] = hasDelta.has(structureId) ? 'delta' : latestCategory[structureId];
  }
  return badges;
}
